import dynamic from 'next/dynamic'
const Section = dynamic(() => import('../components/elements/Section'))
import Perks from '../components/pages/home/Perks'
const Featured = dynamic(() => import('../components/pages/home/Featured'))
const Rentals = dynamic(() => import('../components/pages/common/Rentals'))
const Experience = dynamic(() => import('../components/pages/home/Experience'))
const Reviews = dynamic(() => import('../components/pages/home/Reviews'))

import Welcome from '../components/pages/home/Welcome'
import { useRef, useEffect, useState } from 'react'
import Page from '../components/templates/Page'
import { getS3Url } from '../components/helpers/getS3Url'
import axios from '/components/configs/axios'
import homepageTranslation from '/components/pages/home/homepageTranslation'
import headerTranslation from '/components/general/Header/headerTranslation'
import { useRouter } from 'next/router'
import footerTranslation from '/components/general/Footer/footerTranslations'
import cache from 'memory-cache'
import ctaTranslation from '/components/pages/home/CTA/ctaTranslations'
import Video from '../components/pages/home/Video'
export const getStaticProps = async ({ locale }) => {
  const pageData = await axios
    .get('info/welcome')
    .then((res) => res.data[locale] || null)
    .catch((err) => {
      console.error('Failed to fetch welcome data:', err)
      return null
    })

  //caching for header translations
  let headerDataTranslation = cache.get('headerTranslations')
  if (!headerDataTranslation) {
    headerDataTranslation = await headerTranslation()
    cache.put('headerTranslations', headerDataTranslation)
  }

  const recommendedProperties = await axios
    .get('listings/favourite')
    .then((res) => res.data[locale] || [])
    .catch((err) => {
      console.error('Failed to fetch recommended properties:', err)
      return []
    })

  // caching for footer translations
  let footerDataTranslation = cache.get('footerTranslations')
  if (!footerDataTranslation) {
    footerDataTranslation = await footerTranslation()
    cache.put('footerTranslations', footerDataTranslation)
  }

  // caching for CTA footer translations
  let ctaDataTranslation = cache.get('ctaTranslations')
  if (!ctaDataTranslation) {
    ctaDataTranslation = await ctaTranslation()
    cache.put('ctaTranslations', ctaDataTranslation)
  }

  let pageTranslationData = await homepageTranslation()

  return {
    props: {
      pageData: pageData,
      translations: { page: pageTranslationData },
      headerTranslation: headerDataTranslation,
      footerTranslation: footerDataTranslation,
      ctaTranslation: ctaDataTranslation,
      recommendedProperties
    },
    revalidate: 300
  }
}

const Home = ({
  pageData,
  translations,
  headerTranslation,
  footerTranslation,
  ctaTranslation,
  recommendedProperties
}) => {
  const shadowRef = useRef(null)
  const router = useRouter()

  const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    // Store ref value in local variable to ensure consistency
    const element = containerRef.current

    // Make sure the ref is initialized and points to an actual DOM element
    if (!element || !(element instanceof Element)) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadPlayer(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )

    // Safely observe the element
    observer.observe(element)

    // Clean up function
    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <Page
      headerTranslation={headerTranslation}
      footerTranslation={footerTranslation}
      translations={translations}
      ctaTranslation={ctaTranslation}
      meta={{
        title: translations.page.seo.title[router.locale],
        description: translations.page.seo.title[router.locale]
      }}
    >
      <Welcome
        pageData={pageData}
        translations={translations}
        shadowRef={shadowRef}
        headerTranslation={headerTranslation}
      />

      <Section variant={['white', 'o-hidden']}>
        <Perks
          data={{
            title: translations.page.perks.title[router.locale],
            description: translations.page.perks.description[router.locale],
            button: {
              label: translations.page.perks.button[router.locale],
              href: '/guest-experience'
            },
            image:
              pageData?.cloudflare_introduction_image &&
              getS3Url(pageData?.cloudflare_introduction_image),
            statistics: pageData.introduction_statistics
          }}
          shadowRef={shadowRef}
        />
      </Section>

      <Section separator variant={['center', 'darker']}>
        <Featured translations={translations} pageData={pageData} />
      </Section>

      <div ref={containerRef}>
        <Rentals
          properties={recommendedProperties}
          title={translations.page.rentals.title[router.locale]}
          intro
          buttonLink="/vacation-rentals?homeType=exclusive"
          buttonLabel={translations.page.rentals.button[router.locale]}
          bottomTitle={translations.page.rentals.bottomTitle[router.locale]}
          bottomDescription={
            translations.page.rentals.bottomDescription[router.locale]
          }
          cardTranslation={translations.page.rentals}
        />
      </div>

      {shouldLoadPlayer && (
        <Section variant={['center', 'bottom', 'p0', 'o-hidden']}>
          <Video pageData={pageData} translations={translations} />
        </Section>
      )}

      <Section variant={['white']}>
        <Experience
          title={translations.page.experience.title[router.locale]}
          label={translations.page.experience.label[router.locale]}
          description={translations.page.experience.description[router.locale]}
          translations={translations}
          services={pageData?.service}
        />
      </Section>

      <Section variant={['white', 'center', 'pt0']}>
        <Reviews
          label={translations.page.reviews.label[router.locale]}
          title={translations.page.reviews.title[router.locale]}
          description={translations.page.reviews.description[router.locale]}
          button={translations.page.reviews.getInTouch[router.locale]}
          reviews={pageData.reviews}
        />
      </Section>
    </Page>
  )
}

export default Home
